import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ForcePasswordChange from "./components/ForcePasswordChange";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import NewBookingWizard from "./pages/NewBookingWizard";
import MyBookings from "./pages/MyBookings";
import InvestorBookings from "./pages/InvestorBookings";
import RejectedBookings from "./pages/RejectedBookings";
import AvailabilityPage from "./pages/AvailabilityPage";
import Dashboard from "./pages/Dashboard";
import Customers from "./pages/Customers";
import CustomerProfile from "./pages/CustomerProfile";
import Fleet from "./pages/Fleet";
import Chauffeurs from "./pages/Chauffeurs";
import Bookings from "./pages/Bookings";
import BookingDetail from "./pages/BookingDetail";
import CalendarPage from "./pages/CalendarPage";
import Tours from "./pages/Tours";
import Leads from "./pages/Leads";
import ImportData from "./pages/ImportData";
import Settings from "./pages/Settings";
import DeletedBookings from "./pages/DeletedBookings";
import BookingHistoryDiagnostics from "./pages/BookingHistoryDiagnostics";
import Invoices from "./pages/Invoices";
import Financials from "./pages/Financials";
import SalesPerformance from "./pages/SalesPerformance";
import Tickets from "./pages/Tickets";
import MySchedule from "./pages/MySchedule";
import Tasks from "./pages/Tasks";
import InsuranceList from "./pages/insurance/InsuranceList";
import InsuranceCase from "./pages/insurance/InsuranceCase";
import DealershipApp from "./dealership/DealershipApp";
import MechanicApp from "./mechanic/MechanicApp";
import PublicBook from "./pages/PublicBook";
import AffiliateManagement from "./pages/AffiliateManagement";
import AffiliateLeaderboard from "./pages/AffiliateLeaderboard";
import PayInvoice from "./pages/PayInvoice";
import ViewInvoice from "./pages/ViewInvoice";
import PaymentSuccess from "./pages/PaymentSuccess";
import { permForPath, userHomePath, isChauffeurOnly, canChauffeurSubmit, CHAUFFEUR_ONLY_PATHS, CHAUFFEUR_SUBMIT_PATHS } from "./lib/permissions";
import "./App.css";

// Legacy role route maps: used only for accounts that predate the granular
// permissions array (user.permissions is undefined).
const ROLE_ALLOWED = {
  booking_agent: ["/new-booking", "/my-bookings", "/calendar", "/rejected", "/bookings/view", "/settings"],
  redline: ["/new-booking", "/my-bookings", "/calendar", "/rejected", "/leads", "/bookings/view", "/settings"],
  affiliate: ["/availability", "/leaderboard", "/settings"],
  investor: ["/investor/bookings", "/bookings/view"],
};
const ROLE_HOME = {
  booking_agent: "/new-booking",
  redline: "/new-booking",
  affiliate: "/availability",
  investor: "/investor/bookings",
};

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return (
    <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
      <div className="label-mono">Checking session…</div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;

  // Admins reach everything.
  if (user.role === "admin") return <Layout>{children}</Layout>;

  // Chauffeur-only accounts: locked to Calendar + Fleet Availability (+ Settings
  // for password change). If the `chauffeur_submit` add-on is granted they can
  // ALSO reach New Booking / My Bookings / Rejected / Booking detail. Everything
  // else redirects home.
  if (isChauffeurOnly(user)) {
    const paths = canChauffeurSubmit(user)
      ? [...CHAUFFEUR_ONLY_PATHS, ...CHAUFFEUR_SUBMIT_PATHS]
      : CHAUFFEUR_ONLY_PATHS;
    const allowed = paths.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
    if (!allowed) return <Navigate to="/calendar" replace />;
    return <Layout>{children}</Layout>;
  }

  // Unified access: a route is allowed if (a) it maps to a permission the user
  // was granted, OR (b) it's part of their legacy role's base routes, OR (c) the
  // account is a pure-legacy staff account (no permissions array, no role limits).
  const perm = permForPath(location.pathname);
  const hasPerms = Array.isArray(user.permissions);
  const roleAllowed = ROLE_ALLOWED[user.role];
  const pathMatches = (list) => list && list.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
  // A route can accept ANY ONE of several allowances (e.g. Sales Performance is
  // reachable with either `sales_performance` or the full-team add-on), so the
  // guard must handle a string or an array — same rule permVisible uses.
  const holdsPerm = (need) => {
    if (!need || !hasPerms) return false;
    const list = Array.isArray(need) ? need : [need];
    return list.some((p) => user.permissions.includes(p));
  };

  let ok = false;
  if (holdsPerm(perm)) ok = true;
  if (pathMatches(roleAllowed)) ok = true;
  if (!hasPerms && !roleAllowed) ok = true; // legacy staff = full access

  if (!ok) {
    const home = ROLE_HOME[user.role] || userHomePath(user);
    if (home !== location.pathname) return <Navigate to={home} replace />;
  }
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

// Dealership area: admin-only, renders its own layout (no base sidebar).
function ModuleProtected({ children, perm }) {
  const { user } = useAuth();
  if (user === null) return (
    <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
      <div className="label-mono">Checking session…</div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  const allowed = user.role === "admin" || (Array.isArray(user.permissions) && user.permissions.includes(perm));
  if (!allowed) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="top-right" richColors />
        <ForcePasswordChange />
        <Routes>
          <Route path="/book" element={<PublicBook />} />
          <Route path="/pay/:invoiceId" element={<PayInvoice />} />
          <Route path="/invoice/:invoiceId/view" element={<ViewInvoice />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<div className="min-h-screen flex items-center justify-center bg-neutral-50 text-neutral-700 text-center p-6">Payment cancelled. You can close this window.</div>} />
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/" element={<Protected><Dashboard /></Protected>} />
          <Route path="/new-booking" element={<Protected><NewBookingWizard /></Protected>} />
          <Route path="/my-bookings" element={<Protected><MyBookings /></Protected>} />
          <Route path="/investor/bookings" element={<Protected><InvestorBookings /></Protected>} />
          <Route path="/rejected" element={<Protected><RejectedBookings /></Protected>} />
          <Route path="/availability" element={<Protected><AvailabilityPage /></Protected>} />
          <Route path="/leaderboard" element={<Protected><AffiliateLeaderboard /></Protected>} />
          <Route path="/affiliate-management" element={<Protected><AffiliateManagement /></Protected>} />
          <Route path="/bookings" element={<Navigate to="/bookings/pre" replace />} />
          <Route path="/bookings/view/:id" element={<Protected><BookingDetail /></Protected>} />
          <Route path="/bookings/:section" element={<Protected><Bookings /></Protected>} />
          <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
          <Route path="/customers" element={<Protected><Customers /></Protected>} />
          <Route path="/invoices" element={<Protected><Invoices /></Protected>} />
          <Route path="/financials" element={<Protected><Financials /></Protected>} />
          <Route path="/sales-performance" element={<Protected><SalesPerformance /></Protected>} />
          <Route path="/tickets" element={<Protected><Tickets /></Protected>} />
          <Route path="/customers/:id" element={<Protected><CustomerProfile /></Protected>} />
          <Route path="/fleet" element={<Protected><Fleet /></Protected>} />
          <Route path="/chauffeurs" element={<Protected><Chauffeurs /></Protected>} />
          <Route path="/tours" element={<Protected><Tours /></Protected>} />
          <Route path="/leads" element={<Protected><Leads /></Protected>} />
          <Route path="/import" element={<Protected><ImportData /></Protected>} />
          <Route path="/my-schedule" element={<Protected><MySchedule /></Protected>} />
          <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
          <Route path="/insurance" element={<Protected><InsuranceList /></Protected>} />
          <Route path="/insurance/:id" element={<Protected><InsuranceCase /></Protected>} />
          <Route path="/settings" element={<Protected><Settings /></Protected>} />
          <Route path="/settings/deleted-bookings" element={<Protected><DeletedBookings /></Protected>} />
          <Route path="/settings/booking-history" element={<Protected><BookingHistoryDiagnostics /></Protected>} />
          <Route path="/dealership/*" element={<ModuleProtected perm="dealership"><DealershipApp /></ModuleProtected>} />
          <Route path="/mechanic/*" element={<ModuleProtected perm="mechanic"><MechanicApp /></ModuleProtected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
